import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  Typography,
  Box,
  IconButton,
  Grid2
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PropTypes from 'prop-types';

const QuestionRenderer = ({ question, value, onChange, index, expanded, onExpandChange, error, readOnly = false }) => {
  const handleCheckboxChange = (option) => {
    const newValue = value.includes(option)
      ? value.filter((v) => v !== option)
      : [...value, option];

    if (!question.max || newValue.length <= question.max) {
      onChange(question.qid, newValue);
    }
  };

  const toTitleCase = (str) =>
    str
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

  return (
    <Accordion
      expanded={expanded === index}
      onChange={() => onExpandChange(index)}
      sx={{
        borderRadius: 2,
        mb: 2,
        border: error ? '2px solid #FF0000' : '1px solid #fff',
      }}
    >
      <AccordionSummary
        expandIcon={
          <IconButton
            disableRipple
            sx={{
              backgroundColor: 'rgba(192, 229, 232, 1)',
              width: 32,
              height: 32,
              borderRadius: '50%',
              '&:hover': { backgroundColor: 'rgba(192, 229, 232, 1)' },
            }}
          >
            <ExpandMoreIcon sx={{ color: '#000' }} />
          </IconButton>
        }
        sx={{
          backgroundColor: 'rgba(255, 255, 255, 1)',
          color: '#000',
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
          border: "1px solid #fff",
          minHeight: '56px !important',
        }}
      >
        <Box display="flex" alignItems="center" gap={2}>
          {index + 1}.
          <Typography fontSize="16px">{question.label}</Typography>
        </Box>
      </AccordionSummary>

      <AccordionDetails
        sx={{
          backgroundColor: '#f7f7f7',
          borderBottomLeftRadius: 8,
          borderBottomRightRadius: 8,
        }}
      >
        <FormControl component="fieldset" fullWidth>
          {/* ✅ CHECKBOX QUESTIONS */}
          {question.type === 'checkbox' && (
            <FormGroup>
              <Grid2 container spacing={1}>
                {question.options.map((option) => (
                  <Grid2 item size={{ xs: 12, sm: 6, md: 4 }} key={option}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={value.includes(option)}
                          disabled={readOnly}
                          onChange={() => handleCheckboxChange(option)}
                          sx={{
                            '&.Mui-disabled': {
                              color: value.includes(option) ? '#F6AA48' : '#B7BCC8',
                            },
                          }}
                        />
                      }
                      label={toTitleCase(option)}
                    />
                  </Grid2>
                ))}
              </Grid2>
            </FormGroup>
          )}

          {/* ✅ RADIO QUESTIONS */}
          {question.type === 'radio' && (
            <RadioGroup
              value={value}
              onChange={(e) => !readOnly && onChange(question.qid, e.target.value)}
            >
              <Grid2 container spacing={1}>
                {question.options.map((option) => (
                  <Grid2 item size={{ xs: 12, sm: 6, md: 4 }} key={option}>
                    <FormControlLabel
                      value={option}
                      control={
                        <Radio
                          disabled={readOnly}
                          sx={{
                            '&.Mui-disabled': {
                              color: value === option ? '#F6AA48' : '#B7BCC8',
                            },
                          }}
                        />
                      }
                      label={toTitleCase(option)}
                    />
                  </Grid2>
                ))}
              </Grid2>
            </RadioGroup>
          )}
        </FormControl>
      </AccordionDetails>
    </Accordion>
  );
};

QuestionRenderer.propTypes = {
  question: PropTypes.shape({
    qid: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    options: PropTypes.arrayOf(PropTypes.string),
    max: PropTypes.number,
  }).isRequired,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]).isRequired,
  onChange: PropTypes.func,
  index: PropTypes.number.isRequired,
  expanded: PropTypes.number,
  onExpandChange: PropTypes.func.isRequired,
  error: PropTypes.bool,
  readOnly: PropTypes.bool,
};

export default QuestionRenderer;
